import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { Toast } from "./Toast";
import { cn } from "../../utils/cn";
import { toast, type ToastEvent, type ToastPayload, type ToastVariant } from "../../lib/toast";
import { findOpenModal, measureStackBox, placeToastStack, watchToastStackTop, type ModalDocument } from "../../lib/toastOffset";

/**
 * IMPORTANT: This component must import toast from "../../lib/toast" to ensure
 * it subscribes to the same singleton instance used by callers.
 */

const icons: Record<ToastVariant, string> = {
  default: "lucide:bell",
  success: "lucide:check-circle",
  error: "lucide:alert-triangle",
  warning: "lucide:alert-circle",
  info: "lucide:info"
};

const variantClasses: Record<
  ToastVariant,
  { container: string; title: string; description: string; icon: string; progress: string; progressTrack: string }
> = {
  default: {
    container: "border-border/70 bg-card/90 text-foreground",
    title: "text-foreground",
    description: "text-muted-foreground",
    icon: "text-foreground",
    progress: "bg-foreground/80",
    progressTrack: "bg-border/40"
  },
  success: {
    container:
      "border-emerald-500/80 bg-emerald-700 text-white [--toast-chip-bg:rgb(0_0_0/0.2)]",
    title: "text-white",
    description: "text-white/90",
    icon: "text-white/90",
    progress: "bg-white/90",
    progressTrack: "bg-white/20"
  },
  error: {
    container:
      "border-red-500/80 bg-red-700 text-white [--toast-chip-bg:rgb(0_0_0/0.2)]",
    title: "text-white",
    description: "text-white/90",
    icon: "text-white/90",
    progress: "bg-white/90",
    progressTrack: "bg-white/20"
  },
  warning: {
    container:
      "border-amber-500/80 bg-amber-800 text-white [--toast-chip-bg:rgb(0_0_0/0.2)]",
    title: "text-white",
    description: "text-white/90",
    icon: "text-white/90",
    progress: "bg-white/90",
    progressTrack: "bg-white/20"
  },
  info: {
    container:
      "border-sky-500/80 bg-sky-700 text-white [--toast-chip-bg:rgb(0_0_0/0.2)]",
    title: "text-white",
    description: "text-white/90",
    icon: "text-white/90",
    progress: "bg-white/90",
    progressTrack: "bg-white/20"
  }
};

export const Toaster = () => {
  // Initialize state from module-level store to survive StrictMode remounts
  const [toasts, setToasts] = useState<ToastPayload[]>(() => toast.getToasts());
  const [pausedIds, setPausedIds] = useState<Set<string>>(new Set());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const unsubscribe = toast.subscribe((event: ToastEvent) => {
      if (event.type === "add") {
        setToasts((prev) => [...prev, event.toast]);
        return;
      }

      if (event.type === "dismiss") {
        setToasts((prev) =>
          event.id ? prev.filter((t) => t.id !== event.id) : []
        );
        return;
      }

      if (event.type === "sync") {
        // Sync event: replace state with current store (handles remounts)
        setToasts(event.toasts);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  // While a toast is up and a modal is open, keep the stack clear of that
  // modal's title row, controls and footer, following it as it opens, moves or
  // closes and as the stack grows.
  const stackRef = useRef<HTMLDivElement | null>(null);
  const [stackTop, setStackTop] = useState<number | null>(null);
  const hasToasts = toasts.length > 0;
  useEffect(() => {
    if (!hasToasts) {
      setStackTop(null);
      return;
    }
    const measure = (): number | null => {
      // The DOM satisfies the narrower shape at runtime; `contains` takes a Node there.
      const modal = findOpenModal(document as unknown as ModalDocument, window.innerHeight);
      const container = stackRef.current;
      const stack = container
        ? measureStackBox({
            getBoundingClientRect: () => container.getBoundingClientRect(),
            children: Array.from(container.children).filter((item): item is HTMLElement => item instanceof HTMLElement),
          })
        : null;
      return placeToastStack(modal, stack, window.innerHeight);
    };
    const observe = (notify: () => void): (() => void) => {
      let frame: number | null = null;
      const schedule = (): void => {
        if (frame !== null) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          notify();
        });
      };
      const observer = new MutationObserver(schedule);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["aria-modal"] });
      window.addEventListener("resize", schedule);
      // The entry animation moves a toast without any DOM change; check again
      // once it settles.
      const container = stackRef.current;
      container?.addEventListener("animationend", schedule);
      return () => {
        observer.disconnect();
        window.removeEventListener("resize", schedule);
        container?.removeEventListener("animationend", schedule);
        if (frame !== null) cancelAnimationFrame(frame);
      };
    };
    return watchToastStackTop(measure, setStackTop, observe);
  }, [hasToasts]);

  const handlePause = (id: string) => {
    setPausedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    toast.pause(id);
  };

  const handleResume = (id: string) => {
    setPausedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    toast.resume(id);
  };

  useEffect(() => {
    setPausedIds((prev) => {
      const next = new Set<string>();
      const ids = new Set(toasts.map((toastItem) => toastItem.id));
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
      });
      return next;
    });
  }, [toasts]);

  if (!mounted) return null;

  // The toast layer sits above every modal and dialog layer (z-[9998] to
  // z-[10000]), so feedback raised from inside a modal is never under its backdrop.
  return createPortal(
    <div
      ref={stackRef}
      className="pointer-events-none fixed inset-0 z-[10100] flex flex-col items-end justify-start gap-3 p-6"
      style={stackTop !== null ? { paddingTop: stackTop } : undefined}
      role="status"
      aria-live="polite"
      aria-relevant="additions text"
    >
      {toasts.map((toastItem) => {
        const paused = pausedIds.has(toastItem.id) || toastItem.paused;
        return (
          <div
            key={toastItem.id}
            className="pointer-events-auto toast-enter"
            onMouseEnter={() => handlePause(toastItem.id)}
            onMouseLeave={() => handleResume(toastItem.id)}
            onFocusCapture={() => handlePause(toastItem.id)}
            onBlurCapture={() => handleResume(toastItem.id)}
          >
            <Toast
              title={toastItem.title}
              description={toastItem.description}
              icon={
                <Icon
                  icon={icons[toastItem.type]}
                  className={cn("h-5 w-5", variantClasses[toastItem.type].icon)}
                />
              }
              className={cn(variantClasses[toastItem.type].container, "shadow-soft")}
              titleClassName={variantClasses[toastItem.type].title}
              descriptionClassName={variantClasses[toastItem.type].description}
              progressClassName={variantClasses[toastItem.type].progress}
              progressTrackClassName={variantClasses[toastItem.type].progressTrack}
              progressTotalMs={toastItem.duration}
              progressRemainingMs={toastItem.remainingMs}
              paused={paused}
              onClose={() => toast.dismiss(toastItem.id)}
            />
          </div>
        );
      })}
    </div>,
    document.body
  );
};
